import { Color, Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import { ComponentPropsWithoutRef, MouseEventHandler, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import { paragraphMixins } from 'design-system/mixins/text';
import { Variant } from 'design-system/theme';

export type LabelBackgrounds =
  | 'white'
  | 'blue'
  | 'teal'
  | 'green'
  | 'yellow'
  | 'orange'
  | 'pink'
  | 'red'
  | 'purple'
  | 'darkGrey'
  | 'grey';

export interface StickerProps extends ComponentPropsWithoutRef<'div'> {
  variant?: LabelBackgrounds | Exclude<Variant, 'inverse'>;

  /**
   * Icon to render to the left of the Sticker. 12px icons recommended.
   */
  icon?: ReactNode;

  /**
   * Callback to call upon clicking "x" button. Non-null will render the button.
   */
  onDismiss?: MouseEventHandler;

  /**
   * Additional props to pass to the dismiss "x" button.
   */
  dismissButtonProps?: ComponentPropsWithoutRef<'button'>;

  /**
   * Renders disabled style for the entire Sticker and disables "x" button.
   */
  disabled?: boolean;
}

const StickerIcon = styled('div')<Pick<StickerProps, 'variant'>>`
  height: 12px;
  margin-left: ${Spacing[1]};
  color: ${({ theme, variant }) => {
    switch (variant) {
      case 'red':
        return theme.color.text.inverse;
      default:
        return 'inherit';
    }
  }};
  svg {
    width: 12px;
    height: 12px;
  }
`;

const Content = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  padding: ${Spacing[1]};
`;

const DismissButton = styled('button')<Pick<StickerProps, 'variant' | 'disabled'>>`
  border: none;
  margin: 0;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  padding: ${Spacing[1]};
  height: 100%;
  background: transparent;
  display: inline-flex;
  align-items: center;
  color: ${({ theme, variant, disabled }) => {
    if (variant === 'red') {
      return theme.color.text.inverse;
    }
    return disabled ? 'inherit' : Color.BorderIconPrimary;
  }};

  &${isHovered}${isNotDisabled} {
    cursor: pointer;
    background: ${({ variant }) => {
      switch (variant) {
        case 'blue':
          return Color.BackgroundLabelBlueHover;
        case 'teal':
          return Color.BackgroundLabelTealHover;
        case 'green':
        case 'success':
          return Color.BackgroundLabelGreenHover;
        case 'yellow':
        case 'caution':
          return Color.BackgroundLabelYellowHover;
        case 'orange':
          return Color.BackgroundLabelOrangeHover;
        case 'pink':
        case 'danger':
          return Color.BackgroundLabelRedHover;
        case 'red':
          return Color.BackgroundLabelDarkRedHover;
        case 'purple':
        case 'primary':
          return Color.BackgroundLabelPurpleHover;
        case 'darkGrey':
          return Color.BackgroundLabelDarkGreyHover;
        case 'grey':
        case 'basic':
        default:
          return Color.BackgroundLabelGreyHover;
      }
    }};
  }
`;

const StyledSticker = styled('div')<StickerProps & { sizeIcon?: boolean }>`
  ${paragraphMixins.sm}
  display: inline-flex;
  font-weight: 400;
  white-space: nowrap;
  line-height: 12px;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  box-sizing: border-box;
  min-width: 0px;
  color: ${({ theme, variant, disabled }) => {
    if (disabled) {
      return variant === 'red' ? theme.color.text.inverse : theme.color.text.disabled;
    }
    switch (variant) {
      case 'red':
        return theme.color.text.inverse;
      default:
        return theme.color.text.primary;
    }
  }};
  background: ${({ variant, disabled }) => {
    if (disabled) {
      switch (variant) {
        case 'white':
          return Color.BackgroundLabelWhite;
        case 'blue':
          return Color.BackgroundLabelBlueDisabled;
        case 'teal':
          return Color.BackgroundLabelTealDisabled;
        case 'green':
        case 'success':
          return Color.BackgroundLabelGreenDisabled;
        case 'yellow':
        case 'caution':
          return Color.BackgroundLabelYellowDisabled;
        case 'orange':
          return Color.BackgroundLabelOrangeDisabled;
        case 'pink':
        case 'danger':
          return Color.BackgroundLabelRedDisabled;
        case 'red':
          return Color.BackgroundLabelDarkRedDisabled;
        case 'purple':
        case 'primary':
          return Color.BackgroundLabelPurpleDisabled;
        case 'darkGrey':
          return Color.BackgroundLabelDarkGreyDisabled;
        case 'basic':
        case 'grey':
        default:
          return Color.BackgroundLabelGreyDisabled;
      }
    }
    switch (variant) {
      case 'white':
        return Color.BackgroundLabelWhiteDisabled;
      case 'blue':
        return Color.BackgroundLabelBlue;
      case 'teal':
        return Color.BackgroundLabelTeal;
      case 'green':
      case 'success':
        return Color.BackgroundLabelGreen;
      case 'yellow':
      case 'caution':
        return Color.BackgroundLabelYellow;
      case 'orange':
        return Color.BackgroundLabelOrange;
      case 'pink':
      case 'danger':
        return Color.BackgroundLabelRed;
      case 'red':
        return Color.BackgroundLabelDarkRed;
      case 'purple':
      case 'primary':
        return Color.BackgroundLabelPurple;
      case 'darkGrey':
        return Color.BackgroundLabelDarkGrey;
      case 'basic':
      default:
        return Color.BackgroundLabelGrey;
    }
  }};
  ${({ sizeIcon }) =>
    sizeIcon &&
    css`
      ${StickerIcon} {
        margin: ${Spacing[1]};
      }
    `}
`;

const Sticker = ({
  variant,
  icon,
  children,
  onDismiss,
  disabled,
  dismissButtonProps,
  ...props
}: StickerProps) => {
  const hasContent = children != null;

  if (!hasContent) {
    const hasIcon = icon != null;
    if (!hasIcon) {
      console.warn('Sticker component requires content and/or an icon to render');
      return null;
    }

    if (hasIcon && !props['aria-label']) {
      console.warn('Sticker component requires an aria-label when only rendering an icon');
    }
  }

  return (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    <StyledSticker variant={variant} disabled={disabled} sizeIcon={icon && !hasContent} {...props}>
      {icon && <StickerIcon variant={variant}>{icon}</StickerIcon>}
      {hasContent && (
        <>
          <Content>{children}</Content>
          {onDismiss && (
            <DismissButton
              variant={variant}
              disabled={disabled}
              onClick={onDismiss}
              aria-label='Remove'
              {...dismissButtonProps}
            >
              <XIcon size={12} />
            </DismissButton>
          )}
        </>
      )}
    </StyledSticker>
  );
};

export default Sticker;
